import { useCallback, useEffect, useState } from 'react';

const EMPTY_STATUS = {
  enabled: true,
  publishingEnabled: false,
  publishingAuthorized: false,
  connected: false,
  status: 'disconnected',
  needsReauth: false,
  tokenExpiresAt: null,
  availablePages: [],
  accounts: [],
};

async function readResponse(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || fallbackMessage);
  }
  return data;
}

export function buildFacebookReturnTo(siteId) {
  const params = new URLSearchParams({
    siteId: String(siteId),
    tab: 'Settings',
    settingsSubTab: 'Integrations',
    oauth: 'facebook',
  });
  return `/control-panel?${params.toString()}`;
}

export default function useFacebookConnection(siteId) {
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [error, setError] = useState('');

  const authenticatedRequest = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Please log in again.');
    return fetch(url, {
      ...options,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!siteId) {
      setStatus(EMPTY_STATUS);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await authenticatedRequest(
        `/api/social/facebook/status?siteId=${encodeURIComponent(siteId)}`,
        { cache: 'no-store' }
      );
      setStatus(await readResponse(response, 'Failed to load Facebook connection.'));
    } catch (requestError) {
      setError(requestError.message || 'Failed to load Facebook connection.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedRequest, siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connect = useCallback(async () => {
    if (!siteId) return;
    setAction('connect');
    setError('');
    try {
      const response = await authenticatedRequest('/api/social/facebook/connect', {
        method: 'POST',
        body: JSON.stringify({
          siteId,
          returnTo: buildFacebookReturnTo(siteId),
        }),
      });
      const data = await readResponse(response, 'Failed to start Facebook connection.');
      if (!data.authUrl) throw new Error('Facebook did not provide an authorization URL.');
      window.location.assign(data.authUrl);
    } catch (requestError) {
      setError(requestError.message || 'Failed to start Facebook connection.');
      setAction(null);
    }
  }, [authenticatedRequest, siteId]);

  const selectPages = useCallback(async (pageIds) => {
    if (!siteId) return false;
    setAction('select');
    setError('');
    try {
      const response = await authenticatedRequest('/api/social/facebook/pages', {
        method: 'POST',
        body: JSON.stringify({ siteId, pageIds }),
      });
      await readResponse(response, 'Failed to save Facebook Pages.');
      await refresh();
      return true;
    } catch (requestError) {
      setError(requestError.message || 'Failed to save Facebook Pages.');
      return false;
    } finally {
      setAction(null);
    }
  }, [authenticatedRequest, refresh, siteId]);

  const disconnect = useCallback(async () => {
    if (!siteId) return false;
    setAction('disconnect');
    setError('');
    try {
      const response = await authenticatedRequest('/api/social/facebook/disconnect', {
        method: 'POST',
        body: JSON.stringify({ siteId }),
      });
      await readResponse(response, 'Failed to disconnect Facebook.');
      await refresh();
      return true;
    } catch (requestError) {
      setError(requestError.message || 'Failed to disconnect Facebook.');
      return false;
    } finally {
      setAction(null);
    }
  }, [authenticatedRequest, refresh, siteId]);

  const syncAccount = useCallback(async (accountId, historyWeeks = null) => {
    if (!siteId || !accountId) return null;
    setAction(`sync:${accountId}`);
    setError('');
    try {
      const response = await authenticatedRequest(`/api/social/facebook/accounts/${encodeURIComponent(accountId)}/sync`, {
        method: 'POST',
        body: JSON.stringify({ siteId, ...(historyWeeks ? { historyWeeks } : {}) }),
      });
      const result = await readResponse(response, 'Failed to synchronize Facebook posts.');
      await refresh();
      return result;
    } catch (requestError) {
      setError(requestError.message || 'Failed to synchronize Facebook posts.');
      return null;
    } finally {
      setAction(null);
    }
  }, [authenticatedRequest, refresh, siteId]);

  return { action, connect, disconnect, error, loading, refresh, selectPages, status, syncAccount };
}
